import { useState, type MouseEvent as ReactMouseEvent, type ReactElement } from 'react';

interface IButtonOption {
  value: string;
  src: string;
  title?: string;
}

interface IButtonGroupProps {
  options: IButtonOption[];
  value: string;
  onClick: (value: string) => void;
  showTitle?: boolean;
}

/**
 * ButtonGroup Component
 *
 * A flexible button group component that displays a series of icon-based buttons
 * with selection state management. Supports optional titles below buttons.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for the Schedule view toggle functionality.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/Schedule
 *
 * If this is truly only used for Schedule view switching, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <ButtonGroup
 *   value="calendar"
 *   options={[
 *     { value: 'calendar', src: calendarIcon },
 *     { value: 'list', src: listIcon }
 *   ]}
 *   onClick={handleViewChange}
 * />
 * ```
 */
export const ButtonGroup = ({
  options,
  value,
  onClick,
  showTitle = false,
}: IButtonGroupProps): ReactElement => {
  const [selected, setSelected] = useState<string>(value);

  const handleOnClick = (optionValue: string): void => {
    setSelected(optionValue);
    onClick(optionValue);
  };

  const getClassNames = (index: number, optionValue: string): string => {
    let className = '';

    switch (index) {
      case 0:
        className = 'button_group_container_first_option';
        break;
      case options.length - 1:
        className = 'button_group_container_last_option';
        break;
      default:
        className = 'button_group_container_option';
        break;
    }

    return `${className} ${selected === optionValue ? 'selected_button_group_option' : ''}`;
  };

  const handleButtonClick =
    (optionValue: string) =>
    (event: ReactMouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      handleOnClick(optionValue);
    };

  return (
    <div>
      <div className="button_group_container" style={{ display: 'flex' }}>
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            style={{ all: 'unset' }}
            onClick={handleButtonClick(option.value)}
          >
            <div className={getClassNames(index, option.value)}>
              <img src={option.src} alt={`${option.value} icon`} />
            </div>
          </button>
        ))}
      </div>

      {showTitle && (
        <div style={{ display: 'flex', marginTop: 5, columnGap: 20 }}>
          {options.map((option) => (
            <p key={`title-${option.value}`} className="global_font" style={{ margin: 0 }}>
              {option.title}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
